import * as React from 'react';
import { Upload, X, FileArchive, AlertCircle, Github, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@forgenetwork/ui';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

interface RepositoryUploadProps {
  workspaceId: string;
  onUploadSuccess: () => void;
  onUploadError: (error: string) => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export function RepositoryUpload({
  workspaceId,
  onUploadSuccess,
  onUploadError,
}: RepositoryUploadProps): React.JSX.Element {
  const [activeTab, setActiveTab] = React.useState<'clone' | 'upload'>('clone');
  const [isDragging, setIsDragging] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [repositoryName, setRepositoryName] = React.useState('');
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [isUploading, setIsUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Clone from URL state
  const [repoUrl, setRepoUrl] = React.useState('');
  const [isCloning, setIsCloning] = React.useState(false);

  const validateFile = (file: File): string | null => {
    if (!file.name.endsWith('.zip')) {
      return 'Only ZIP files are allowed';
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    return null;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    const file = files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileSelect = (file: File): void => {
    setError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSelectedFile(file);
    // Extract name from filename (remove .zip extension)
    const defaultName = file.name.replace(/\.zip$/i, '');
    setRepositoryName(defaultName);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    const file = files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile || !repositoryName.trim()) {
      setError('Please provide a repository name');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Step 1: Create the repository (10% progress)
      setUploadProgress(5);
      const createResponse = await api.post<{ repository: { id: string } }>(
        `/workspaces/${workspaceId}/repositories`,
        { name: repositoryName.trim() }
      );

      if (!createResponse?.repository?.id) {
        throw new Error('Failed to create repository');
      }

      const repositoryId = createResponse.repository.id;
      setUploadProgress(10);

      // Step 2: Upload the ZIP file with progress tracking
      const formData = new FormData();
      formData.append('file', selectedFile);

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            // Map upload progress from 10% to 100%
            const uploadPercent = (e.loaded / e.total) * 90;
            setUploadProgress(10 + uploadPercent);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || errorData.message || 'Upload failed'));
            } catch {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.open('POST', `/api/v1/repositories/${repositoryId}/upload`);
        // Cookies are sent automatically with credentials
        xhr.withCredentials = true;
        xhr.send(formData);
      });

      setIsUploading(false);
      setSelectedFile(null);
      setRepositoryName('');
      setUploadProgress(0);
      onUploadSuccess();
    } catch (err) {
      setIsUploading(false);
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      onUploadError(errorMessage);
    }
  };

  const handleRemoveFile = (): void => {
    setSelectedFile(null);
    setRepositoryName('');
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = (): void => {
    fileInputRef.current?.click();
  };

  const validateRepoUrl = (url: string): string | null => {
    if (!url.trim()) {
      return 'Please enter a repository URL';
    }
    const urlPattern = /^https:\/\/(github\.com|gitlab\.com)\/.+\/.+$/;
    if (!urlPattern.test(url)) {
      return 'Please enter a valid GitHub or GitLab repository URL (e.g., https://github.com/owner/repo)';
    }
    return null;
  };

  const handleClone = async (): Promise<void> => {
    const validationError = validateRepoUrl(repoUrl);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsCloning(true);
    setError(null);

    try {
      await api.post('/git/clone', {
        url: repoUrl.trim(),
        workspaceId,
      });

      setIsCloning(false);
      setRepoUrl('');
      onUploadSuccess();
    } catch (err) {
      setIsCloning(false);
      const errorMessage = err instanceof Error ? err.message : 'Failed to clone repository';
      setError(errorMessage);
      onUploadError(errorMessage);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'clone' | 'upload')}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="clone" className="flex items-center gap-2">
              <Github className="h-4 w-4" />
              Clone from URL
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload ZIP
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clone">
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center py-8 px-6 text-center border-2 border-dashed border-muted-foreground/25 rounded-lg">
                <Github className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  Clone Repository
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Enter a GitHub or GitLab repository URL to clone
                </p>
                <div className="w-full max-w-md space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="repo-url">Repository URL</Label>
                    <div className="relative">
                      <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="repo-url"
                        type="url"
                        placeholder="https://github.com/owner/repository"
                        value={repoUrl}
                        onChange={(e) => {
                          setRepoUrl(e.target.value);
                          setError(null);
                        }}
                        className="pl-10"
                        disabled={isCloning}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleClone}
                    className="w-full"
                    disabled={!repoUrl.trim() || isCloning}
                  >
                    {isCloning ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cloning Repository...
                      </>
                    ) : (
                      <>
                        <Github className="mr-2 h-4 w-4" />
                        Clone Repository
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-6">
                  Supports public GitHub and GitLab repositories
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="upload">
            <div
              className={cn(
                'relative rounded-lg border-2 border-dashed transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25',
                isUploading && 'pointer-events-none opacity-50'
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {!selectedFile && !isUploading && (
                <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                  <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">
                    Upload Repository
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Drag and drop a ZIP file here, or click to browse
                  </p>
                  <Button type="button" variant="outline" onClick={handleBrowseClick}>
                    Browse Files
                  </Button>
                  <p className="text-xs text-muted-foreground mt-4">
                    Maximum file size: 50MB
                  </p>
                </div>
              )}

              {selectedFile && !isUploading && (
                <div className="py-8 px-6">
                  <div className="flex items-center gap-4">
                    <FileArchive className="h-10 w-10 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{selectedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleRemoveFile}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="repository-name">Repository Name</Label>
                      <Input
                        id="repository-name"
                        type="text"
                        placeholder="Enter repository name"
                        value={repositoryName}
                        onChange={(e) => setRepositoryName(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={handleUpload}
                        className="flex-1"
                        disabled={!repositoryName.trim()}
                      >
                        Upload Repository
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleBrowseClick}
                      >
                        Choose Different File
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {isUploading && (
                <div className="py-8 px-6">
                  <div className="flex items-center gap-4 mb-4">
                    <FileArchive className="h-10 w-10 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{selectedFile?.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Uploading... {uploadProgress.toFixed(0)}%
                      </p>
                    </div>
                  </div>
                  <Progress value={uploadProgress} />
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".zip"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </TabsContent>
        </Tabs>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-destructive">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
